import React from 'react'
import {
  Box,
  Card,
  CardActionArea,
  Typography
} from '@mui/material'
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import StatusBadge from './StatusBadge'
import { TREND_DIRECTION } from '../../utils/trendUtils'
import { formatParameterValue } from '../../utils/trendUtils'

/**
 * ParameterCard - An at-a-glance card showing a pool parameter's current value,
 * status, and trend direction. Tappable to navigate to /add-chemicals when
 * the parameter is not in the "Good" range.
 *
 * @param {Object} props
 * @param {string} props.parameterKey - The measurement field name (e.g. 'free_chlorine')
 * @param {string} props.label - Full parameter name (e.g. 'Free Chlorine')
 * @param {string} props.shortLabel - Abbreviated label (e.g. 'FC')
 * @param {string} props.unit - Unit string (e.g. 'ppm', '')
 * @param {string} props.color - Hex color for the parameter
 * @param {number|null} props.value - Current parameter value
 * @param {string} props.trend - One of TREND_DIRECTION values
 * @param {string} props.statusLabel - Status text (e.g. 'Good', 'Low', 'High')
 * @param {string} props.statusColor - MUI color for StatusBadge (e.g. 'success', 'warning', 'error')
 */
const ParameterCard = ({
  parameterKey,
  label,
  shortLabel,
  unit,
  color,
  value,
  trend,
  statusLabel,
  statusColor
}) => {
  const navigate = useNavigate()
  const isActionNeeded = statusLabel && statusLabel !== 'Good' && statusLabel !== 'Unknown'

  const handleClick = () => {
    if (isActionNeeded) {
      navigate('/add-chemicals')
    }
  }

  const renderTrendIcon = () => {
    const iconSx = { fontSize: 20 }
    switch (trend) {
      case TREND_DIRECTION.UP:
        return <TrendingUpIcon sx={{ ...iconSx, color: 'success.main' }} />
      case TREND_DIRECTION.DOWN:
        return <TrendingDownIcon sx={{ ...iconSx, color: 'error.main' }} />
      case TREND_DIRECTION.FLAT:
        return <TrendingFlatIcon sx={{ ...iconSx, color: 'text.secondary' }} />
      default:
        return null
    }
  }

  const cardContent = (
    <Box
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 120
      }}
    >
      {/* Parameter label */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontSize: '0.65rem'
        }}
      >
        {shortLabel}
      </Typography>

      {/* Value + unit */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: color,
            lineHeight: 1.2
          }}
        >
          {formatParameterValue(value, parameterKey)}
        </Typography>
        {unit && value !== null && value !== undefined && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
          >
            {unit}
          </Typography>
        )}
      </Box>

      {/* Trend arrow */}
      <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center' }}>
        {renderTrendIcon()}
      </Box>

      {/* Status badge */}
      {statusLabel && (
        <StatusBadge
          label={statusLabel}
          color={statusColor || 'info'}
          size="small"
        />
      )}
    </Box>
  )

  // If action is needed, wrap in CardActionArea for tap feedback
  if (isActionNeeded) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderColor: statusColor === 'error' ? 'error.main' : 'warning.main',
          borderWidth: 2,
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: 2
          }
        }}
      >
        <CardActionArea onClick={handleClick}>
          {cardContent}
        </CardActionArea>
      </Card>
    )
  }

  return (
    <Card variant="outlined">
      {cardContent}
    </Card>
  )
}

export default ParameterCard
